import { create } from "zustand"
import { persist } from "zustand/middleware"
import {
  SHARED_TIMELINE_PERSISTED_TAIL_RESTORE_THRESHOLD_PX,
} from "@/lib/shared-timeline-viewport"
import {
  normalizeSharedTimelineViewportAnchor,
  sameSharedTimelineViewportAnchor,
  type SharedTimelineViewportAnchor,
} from "@/lib/shared-timeline-viewport-anchor"
import { useTerminalStore } from "@/stores/terminal"

/**
 * High-level content panel mode.
 * - "file-tab": A file tab is active — the tab's own `viewMode` determines what renders (code, edit, diff, markdown-preview, html-preview).
 * - "preview": Live preview iframe is showing.
 * - "devlogs": Dev server logs panel is showing.
 * - null: Content panel is closed.
 */
export type ContentPanelMode = "file-tab" | "preview" | "devlogs" | null

/** The viewing mode of an individual file tab. */
export type TabViewMode = "code" | "edit" | "diff" | "markdown-preview" | "html-preview"
export type MobileSurface = "chat" | "files" | "changes" | "tools"
export type RightSidebarTab = "files" | "changes"
export type RightSidebarChangesAutoSwitchState = "pending" | "settled"
export type ChangesViewMode = "flat" | "tree"

// 300px keeps the main sidebar wide enough for the top navigation border to
// align with the icon rail while leaving comfortable room on common laptop
// widths; very narrow desktop windows can still compress via the hard minimum.
export const LEFT_SIDEBAR_DEFAULT_WIDTH = 300
export const LEFT_SIDEBAR_MIN_WIDTH = 300
export const LEFT_SIDEBAR_HARD_MIN_WIDTH = 120

// Match the left sidebar's practical minimum so file/change rows have enough
// room for names and status metadata without feeling cramped in normal layouts.
export const RIGHT_SIDEBAR_DEFAULT_WIDTH = 300
export const RIGHT_SIDEBAR_MIN_WIDTH = 300
export const RIGHT_SIDEBAR_HARD_MIN_WIDTH = 120

// Tree view mirrors the repository shape and is easier to scan by default; users
// can still switch to the flat list, which we then persist as their preference.
export const DEFAULT_CHANGES_VIEW_MODE: ChangesViewMode = "tree"

export const CHAT_PANEL_DEFAULT_WIDTH_PERCENT = 50
export const CHAT_PANEL_MIN_WIDTH_PERCENT = 20
export const CHAT_PANEL_MAX_WIDTH_PERCENT = 80

// Increment when persisted layout preference semantics change so localStorage
// snapshots are normalized before they hydrate the store.
export const SESSION_UI_PERSIST_VERSION = 5

function clampPersistedNumber(value: unknown, fallback: number, min: number, max: number) {
  const numeric = typeof value === "number" && Number.isFinite(value) ? value : fallback
  return Math.min(max, Math.max(min, numeric))
}

/** A single tab in the content panel tab bar. */
export interface ContentTab {
  /** Unique id — just the file path (e.g. "src/App.tsx"). One tab per file. */
  id: string
  path: string
  /** Current viewing mode. Mutable — toggling code/diff/edit/preview changes this in-place. */
  viewMode: TabViewMode
  /** Preview tabs are displayed in italics and get replaced on single-click. */
  isPreview: boolean
}

export interface SessionLayoutSnapshot {
  terminalVisible: boolean
  mobileSurface: MobileSurface
  contentPanelMode: ContentPanelMode
  tabs: ContentTab[]
  activeTabId: string | null
  rightSidebarOpen: boolean
  rightSidebarTab: RightSidebarTab
  rightSidebarChangesAutoSwitchState: RightSidebarChangesAutoSwitchState
  expandedDirs: string[]
  changesExpandedDirs: string[]
  todoDockOpen: boolean
  todoDockAutoOpenSuppressed: boolean
  debugLogsOpen: boolean
  sharedTimelineScrollOffsetFromBottom: number | null
  sharedTimelineViewportAnchor: SharedTimelineViewportAnchor | null
}

function normalizeLineNumber(lineNumber?: number): number | null {
  if (typeof lineNumber !== "number" || !Number.isFinite(lineNumber)) return null
  const normalized = Math.trunc(lineNumber)
  return normalized > 0 ? normalized : null
}

// Track file modification timeouts outside the store to enable cleanup
const fileModifiedTimers = new Map<string, ReturnType<typeof setTimeout>>()

function clearFileModifiedTimers(): void {
  for (const timer of fileModifiedTimers.values()) clearTimeout(timer)
  fileModifiedTimers.clear()
}

function resetTransientUiState(): Pick<SessionUIState,
  | "recentlyModifiedFiles"
  | "staleOpenFile"
  | "fileDrafts"
  | "gitOperationLoading"
  | "gitOperationError"
  | "generatedCommitMessage"
  | "commitMessageGenerating"
  | "commitMessageError"
  | "prDescGenerating"
  | "prDescError"
  | "generatedPrTitle"
  | "generatedPrBody"
  | "dismissedQuickPrOperationIds"
> {
  return {
    recentlyModifiedFiles: new Map<string, number>(),
    staleOpenFile: false,
    fileDrafts: {},
    gitOperationLoading: false,
    gitOperationError: null,
    generatedCommitMessage: null,
    commitMessageGenerating: false,
    commitMessageError: null,
    prDescGenerating: false,
    prDescError: null,
    generatedPrTitle: null,
    generatedPrBody: null,
    dismissedQuickPrOperationIds: {},
  }
}

function normalizeSnapshotViewMode(value: unknown): TabViewMode {
  if (value === "code" || value === "edit" || value === "diff" || value === "markdown-preview" || value === "html-preview") return value
  return "code"
}

function normalizeSnapshotTab(value: unknown): ContentTab | null {
  if (!value || typeof value !== "object") return null
  const row = value as Record<string, unknown>
  const path = typeof row.path === "string" ? row.path : ""
  if (!path) return null
  // Support legacy snapshots that stored `mode: "file" | "diff"` instead of `viewMode`
  const legacyMode = row.mode
  const viewMode: TabViewMode = row.viewMode != null
    ? normalizeSnapshotViewMode(row.viewMode)
    : legacyMode === "diff" ? "diff" : "code"
  const isPreview = typeof row.isPreview === "boolean" ? row.isPreview : false
  // Tab IDs are now just the path. Normalize legacy `mode:path` IDs.
  const id = path
  return { id, path, viewMode, isPreview }
}

function normalizeSnapshotMode(value: unknown): ContentPanelMode {
  if (value === "file-tab" || value === "preview" || value === "devlogs") return value
  // Legacy: "file" and "diff" both map to "file-tab" (the tab's viewMode carries the detail)
  if (value === "file" || value === "diff") return "file-tab"
  return null
}

function normalizeSnapshotDirArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const normalized: string[] = []
  for (const item of value) {
    if (typeof item !== "string" || item.length === 0) continue
    normalized.push(item)
  }
  return Array.from(new Set(normalized))
}

function normalizeSnapshotBoolean(value: unknown): boolean {
  return typeof value === "boolean" ? value : false
}

function normalizeSnapshotBooleanWithFallback(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback
}

function normalizeSnapshotMobileSurface(value: unknown): MobileSurface {
  return value === "files" || value === "changes" || value === "tools" ? value : "chat"
}

function normalizeRightSidebarChangesAutoSwitchState(value: unknown): RightSidebarChangesAutoSwitchState {
  if (value === "pending" || value === "settled") return value
  // Legacy snapshots did not record whether automatic tab selection was still
  // allowed. Treat them as settled so a restored session preserves the tab the
  // user last saw instead of unexpectedly switching on old existing changes.
  return "settled"
}

function normalizeNonNegativeSnapshotScrollOffset(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null
  const normalized = Math.floor(value)
  return normalized >= 0 ? normalized : null
}

function normalizeSnapshotScrollOffset(value: unknown): number | null {
  const normalized = normalizeNonNegativeSnapshotScrollOffset(value)
  return normalized !== null && normalized > SHARED_TIMELINE_PERSISTED_TAIL_RESTORE_THRESHOLD_PX
    ? normalized
    : null
}

interface SessionUIState {
  // Left sidebar
  leftSidebarOpen: boolean
  leftSidebarWidth: number
  toggleLeftSidebar: () => void
  setLeftSidebarOpen: (open: boolean) => void
  setLeftSidebarWidth: (width: number) => void

  // Right sidebar
  rightSidebarOpen: boolean
  rightSidebarWidth: number
  rightSidebarTab: RightSidebarTab
  rightSidebarChangesAutoSwitchState: RightSidebarChangesAutoSwitchState
  changesViewMode: ChangesViewMode
  changesViewModeUserSelected: boolean
  toggleRightSidebar: () => void
  setRightSidebarOpen: (open: boolean) => void
  setRightSidebarWidth: (width: number) => void
  setRightSidebarTab: (tab: RightSidebarTab) => void
  autoSwitchRightSidebarToChanges: () => void
  setChangesViewMode: (mode: ChangesViewMode) => void

  // Terminal
  terminalVisible: boolean
  terminalHeight: number // percentage of viewport (default 35)
  toggleTerminal: () => void
  setTerminalHeight: (height: number) => void

  // Mobile shell
  mobileSurface: MobileSurface
  setMobileSurface: (surface: MobileSurface) => void

  // Content panel (file viewer + preview) — multi-tab
  contentPanelMode: ContentPanelMode
  openFilePath: string | null
  targetLine: number | null
  tabs: ContentTab[]
  activeTabId: string | null
  /** Unsaved editor drafts keyed by repo-relative file path. */
  fileDrafts: Record<string, string>
  chatPanelWidth: number // percentage of center row width when content panel is open

  /**
   * Open a file tab.
   * - `preview: true` (default for single-click): replaces the existing preview
   *   tab if one exists, or creates a new preview tab.
   * - `preview: false` (double-click / pinning): opens a permanent tab, or
   *   promotes an existing preview tab for the same path.
   */
  openFile: (path: string, viewMode?: TabViewMode, preview?: boolean, lineNumber?: number) => void
  /** Switch the viewMode of an existing tab in-place (used by the segmented toggle). */
  setTabViewMode: (tabId: string, viewMode: TabViewMode) => void
  setFileDraft: (path: string, content: string) => void
  clearFileDraft: (path: string) => void
  clearFileDrafts: (paths: string[]) => void
  renameOpenFile: (oldPath: string, newPath: string) => void
  /** Promote the current preview tab to permanent (e.g. when user starts editing). */
  pinTab: (tabId: string) => void
  closeTab: (tabId: string) => void
  closeActiveTab: () => void
  nextFileTab: () => void
  previousFileTab: () => void
  setActiveTab: (tabId: string) => void
  clearTargetLine: () => void
  setChatPanelWidth: (width: number) => void
  openPreview: () => void
  openDevServerLogs: () => void
  closeContentPanel: () => void

  // Chat todo dock
  todoDockOpen: boolean
  todoDockAutoOpenSuppressed: boolean
  setTodoDockOpen: (open: boolean) => void
  setTodoDockAutoOpenSuppressed: (suppressed: boolean) => void

  // Shared timeline chat viewport state
  sharedTimelineScrollOffsetFromBottom: number | null
  sharedTimelineViewportAnchor: SharedTimelineViewportAnchor | null
  setSharedTimelineViewState: (state: {
    scrollOffsetFromBottom?: number | null
    viewportAnchor?: SharedTimelineViewportAnchor | null
  }) => void

  // Debug logs panel
  debugLogsOpen: boolean
  setDebugLogsOpen: (open: boolean) => void
  toggleDebugLogs: () => void

  // File tree
  expandedDirs: Set<string>
  toggleDir: (path: string) => void
  setExpandedDirs: (dirs: Set<string>) => void

  // Changes panel tree
  changesExpandedDirs: Set<string>
  toggleChangesDir: (path: string) => void
  setChangesExpandedDirs: (dirs: Set<string>) => void

  // Recently modified files (for activity indicator)
  recentlyModifiedFiles: Map<string, number>
  markFileModified: (path: string) => void

  // Stale open file (agent modified the currently viewed file)
  staleOpenFile: boolean
  setStaleOpenFile: (stale: boolean) => void

  // Git operation state (for push/pull loading indicators)
  gitOperationLoading: boolean
  gitOperationError: string | null
  setGitOperationLoading: (loading: boolean) => void
  setGitOperationError: (error: string | null) => void

  // AI commit message generation
  generatedCommitMessage: string | null
  commitMessageGenerating: boolean
  commitMessageError: string | null
  setGeneratedCommitMessage: (msg: string | null) => void
  setCommitMessageGenerating: (loading: boolean) => void
  setCommitMessageError: (error: string | null) => void

  // AI PR description generation
  prDescGenerating: boolean
  prDescError: string | null
  generatedPrTitle: string | null
  generatedPrBody: string | null
  setPrDescGenerating: (loading: boolean) => void
  setPrDescError: (error: string | null) => void
  setGeneratedPrTitle: (title: string | null) => void
  setGeneratedPrBody: (body: string | null) => void

  // Quick PR UI presentation state
  dismissedQuickPrOperationIds: Record<string, string | null>
  dismissQuickPrOperation: (sessionId: string, operationId: string) => void
  clearDismissedQuickPrOperation: (sessionId: string) => void

  // New-session centered mode — hides the right sidebar and header until the first message is sent.
  // This is a transient UI state (not persisted) that drives the "clean start" experience.
  newSessionCentered: boolean
  setNewSessionCentered: (centered: boolean) => void

  // Reset ephemeral state when switching sessions
  resetEphemeral: () => void
}

const SESSION_LAYOUT_SNAPSHOT_STORAGE_PREFIX = "cord-session-layout-snapshot:"
// Keep a bounded, in-memory map so we can restore per-thread layout state while
// avoiding unbounded growth if a user touches many sessions in one tab.
const MAX_LOCAL_SESSION_LAYOUT_SNAPSHOTS = 100
const sessionLayoutSnapshotsBySessionId = new Map<string, SessionLayoutSnapshot>()

function buildSessionLayoutSnapshotStorageKey(sessionId: string): string {
  return `${SESSION_LAYOUT_SNAPSHOT_STORAGE_PREFIX}${sessionId}`
}

function loadSessionLayoutSnapshotFromStorage(sessionId: string): SessionLayoutSnapshot | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null
  }
  try {
    const raw = window.localStorage.getItem(buildSessionLayoutSnapshotStorageKey(sessionId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== "object") return null
    return parsed as SessionLayoutSnapshot
  } catch {
    return null
  }
}

function persistSessionLayoutSnapshotToStorage(sessionId: string, snapshot: SessionLayoutSnapshot): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return
  }
  try {
    window.localStorage.setItem(
      buildSessionLayoutSnapshotStorageKey(sessionId),
      JSON.stringify(snapshot),
    )
  } catch {
    // Ignore storage quota and serialization errors. In-memory restore still works.
  }
}

function removeSessionLayoutSnapshotFromStorage(sessionId: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return
  }
  try {
    window.localStorage.removeItem(buildSessionLayoutSnapshotStorageKey(sessionId))
  } catch {
    // Ignore storage errors during cleanup.
  }
}

function pruneSessionLayoutSnapshotsIfNeeded(): void {
  while (sessionLayoutSnapshotsBySessionId.size > MAX_LOCAL_SESSION_LAYOUT_SNAPSHOTS) {
    const firstKey = sessionLayoutSnapshotsBySessionId.keys().next().value
    if (typeof firstKey !== "string") break
    sessionLayoutSnapshotsBySessionId.delete(firstKey)
    removeSessionLayoutSnapshotFromStorage(firstKey)
  }
}

export function normalizePersistedSessionUIState(persistedState: unknown): Record<string, unknown> {
  const state = (persistedState ?? {}) as Record<string, unknown>
  const leftSidebarOpen = typeof state.leftSidebarOpen === "boolean"
    ? state.leftSidebarOpen
    : true
  const rightSidebarOpen = typeof state.rightSidebarOpen === "boolean"
    ? state.rightSidebarOpen
    : false
  const changesViewModeUserSelected = state.changesViewModeUserSelected === true
  const persistedChangesViewMode = state.changesViewMode === "flat" ? "flat" : DEFAULT_CHANGES_VIEW_MODE
  const changesViewMode = changesViewModeUserSelected ? persistedChangesViewMode : DEFAULT_CHANGES_VIEW_MODE

  return {
    ...state,
    leftSidebarOpen,
    leftSidebarWidth: clampPersistedNumber(
      state.leftSidebarWidth,
      LEFT_SIDEBAR_DEFAULT_WIDTH,
      LEFT_SIDEBAR_MIN_WIDTH,
      Infinity,
    ),
    rightSidebarOpen,
    rightSidebarWidth: clampPersistedNumber(
      state.rightSidebarWidth,
      RIGHT_SIDEBAR_DEFAULT_WIDTH,
      RIGHT_SIDEBAR_MIN_WIDTH,
      Infinity,
    ),
    changesViewMode,
    changesViewModeUserSelected,
    chatPanelWidth: clampPersistedNumber(
      state.chatPanelWidth,
      CHAT_PANEL_DEFAULT_WIDTH_PERCENT,
      CHAT_PANEL_MIN_WIDTH_PERCENT,
      CHAT_PANEL_MAX_WIDTH_PERCENT,
    ),
    terminalHeight: clampPersistedNumber(state.terminalHeight, 25, 15, 70),
  }
}

export const useSessionUI = create<SessionUIState>()(
  persist(
    (set, get) => ({
      // Left sidebar
      leftSidebarOpen: true,
      leftSidebarWidth: LEFT_SIDEBAR_DEFAULT_WIDTH,
      toggleLeftSidebar: () =>
        set((s) => ({ leftSidebarOpen: !s.leftSidebarOpen })),
      setLeftSidebarOpen: (open) => set({ leftSidebarOpen: open }),
      setLeftSidebarWidth: (width) =>
        set({
          leftSidebarWidth: Math.max(LEFT_SIDEBAR_HARD_MIN_WIDTH, width),
        }),

      // Right sidebar
      rightSidebarOpen: false,
      rightSidebarWidth: RIGHT_SIDEBAR_DEFAULT_WIDTH,
      rightSidebarTab: "files" as const,
      rightSidebarChangesAutoSwitchState: "pending" as const,
      changesViewMode: DEFAULT_CHANGES_VIEW_MODE,
      changesViewModeUserSelected: false,
      toggleRightSidebar: () =>
        set((s) => ({ rightSidebarOpen: !s.rightSidebarOpen })),
      setRightSidebarOpen: (open) => set({ rightSidebarOpen: open }),
      setRightSidebarWidth: (width) =>
        set({
          rightSidebarWidth: Math.max(RIGHT_SIDEBAR_HARD_MIN_WIDTH, width),
        }),
      setRightSidebarTab: (tab) => set({
        rightSidebarTab: tab,
        rightSidebarChangesAutoSwitchState: "settled" as const,
      }),
      autoSwitchRightSidebarToChanges: () => set((state) => {
        if (state.rightSidebarChangesAutoSwitchState !== "pending") return state
        return {
          rightSidebarTab: "changes" as const,
          rightSidebarChangesAutoSwitchState: "settled" as const,
        }
      }),
      setChangesViewMode: (mode) => set({
        changesViewMode: mode,
        changesViewModeUserSelected: true,
      }),

      // Terminal
      terminalVisible: false,
      terminalHeight: 25,
      toggleTerminal: () =>
        set((s) => ({ terminalVisible: !s.terminalVisible })),
      setTerminalHeight: (height) =>
        set({ terminalHeight: Math.min(70, Math.max(15, height)) }),

      // Mobile shell
      mobileSurface: "chat",
      setMobileSurface: (surface) => set({ mobileSurface: surface }),

      // Content panel — multi-tab
      contentPanelMode: null,
      openFilePath: null,
      targetLine: null,
      tabs: [],
      activeTabId: null,
      fileDrafts: {},
      chatPanelWidth: CHAT_PANEL_DEFAULT_WIDTH_PERCENT,
      todoDockOpen: false,
      todoDockAutoOpenSuppressed: false,
      sharedTimelineScrollOffsetFromBottom: null,
      sharedTimelineViewportAnchor: null,

      openFile: (path, viewMode = "code", preview = true, lineNumber) => {
        const state = get()
        const tabId = path // Tab IDs are now just the file path
        const targetLine = normalizeLineNumber(lineNumber)
        const shouldPinForEdit = viewMode === "edit"

        // If a tab for this path already exists, activate it and update its viewMode
        const existing = state.tabs.find((t) => t.id === tabId)
        if (existing) {
          const updatedTabs = state.tabs.map((t) => {
            if (t.id !== tabId) return t
            return {
              ...t,
              viewMode,
              isPreview: shouldPinForEdit ? false : (preview ? t.isPreview : false),
            }
          })
          set({
            tabs: updatedTabs,
            activeTabId: tabId,
            contentPanelMode: "file-tab",
            openFilePath: path,
            targetLine,
            staleOpenFile: false,
          })
          return
        }

        let newTabs = [...state.tabs]

        if (preview && !shouldPinForEdit) {
          // Replace existing preview tab (if any) with this new preview
          const previewIdx = newTabs.findIndex((t) => t.isPreview)
          const newTab: ContentTab = { id: tabId, path, viewMode, isPreview: true }
          if (previewIdx >= 0) {
            newTabs[previewIdx] = newTab
          } else {
            newTabs.push(newTab)
          }
        } else {
          // Add permanent tab. Also remove any preview tab for the same file.
          newTabs = newTabs.filter(
            (t) => !(t.path === path && t.isPreview),
          )
          newTabs.push({ id: tabId, path, viewMode, isPreview: false })
        }

        set({
          tabs: newTabs,
          activeTabId: tabId,
          contentPanelMode: "file-tab",
          openFilePath: path,
          targetLine,
          staleOpenFile: false,
        })
      },

      setTabViewMode: (tabId, viewMode) => {
        const state = get()
        const tab = state.tabs.find((t) => t.id === tabId)
        if (!tab) return
        set({
          tabs: state.tabs.map((t) =>
            t.id === tabId
              ? {
                  ...t,
                  viewMode,
                  // Entering edit mode should always pin the tab so single-click
                  // preview replacement cannot silently discard the editing surface.
                  isPreview: viewMode === "edit" ? false : t.isPreview,
                }
              : t,
          ),
        })
      },

      setFileDraft: (path, content) =>
        set((state) => ({
          fileDrafts: {
            ...state.fileDrafts,
            [path]: content,
          },
        })),

      clearFileDraft: (path) =>
        set((state) => {
          if (!(path in state.fileDrafts)) return state
          const next = { ...state.fileDrafts }
          delete next[path]
          return { fileDrafts: next }
        }),

      clearFileDrafts: (paths) =>
        set((state) => {
          let changed = false
          const next = { ...state.fileDrafts }
          for (const path of paths) {
            if (path in next) {
              delete next[path]
              changed = true
            }
          }
          return changed ? { fileDrafts: next } : state
        }),

      renameOpenFile: (oldPath, newPath) => {
        if (oldPath === newPath) return

        set((state) => {
          let tabsChanged = false
          let nextActiveTabId = state.activeTabId
          const dedupedTabs: ContentTab[] = []
          const seenTabIds = new Set<string>()

          for (const tab of state.tabs) {
            const nextTab = tab.path === oldPath
              ? { ...tab, path: newPath, id: newPath }
              : tab

            if (nextTab !== tab) {
              tabsChanged = true
            }

            if (state.activeTabId === tab.id) {
              nextActiveTabId = nextTab.id
            }

            if (!seenTabIds.has(nextTab.id)) {
              dedupedTabs.push(nextTab)
              seenTabIds.add(nextTab.id)
            }
          }

          const nextOpenFilePath = state.openFilePath === oldPath ? newPath : state.openFilePath
          const nextFileDrafts = { ...state.fileDrafts }
          if (oldPath in nextFileDrafts) {
            nextFileDrafts[newPath] = nextFileDrafts[oldPath]
            delete nextFileDrafts[oldPath]
          }
          const draftsChanged = nextFileDrafts !== state.fileDrafts && (
            nextFileDrafts[newPath] !== state.fileDrafts[newPath]
            || nextFileDrafts[oldPath] !== state.fileDrafts[oldPath]
          )
          if (!tabsChanged && nextOpenFilePath === state.openFilePath && !draftsChanged) {
            return state
          }

          return {
            tabs: dedupedTabs,
            activeTabId: nextActiveTabId,
            openFilePath: nextOpenFilePath,
            fileDrafts: nextFileDrafts,
            staleOpenFile: false,
          }
        })
      },

      pinTab: (tabId) =>
        set((s) => ({
          tabs: s.tabs.map((t) =>
            t.id === tabId ? { ...t, isPreview: false } : t,
          ),
        })),

      closeTab: (tabId) => {
        const state = get()
        const idx = state.tabs.findIndex((t) => t.id === tabId)
        if (idx < 0) return

        const newTabs = state.tabs.filter((t) => t.id !== tabId)

        if (newTabs.length === 0) {
          // No tabs left — close the content panel entirely
          set({
            tabs: [],
            activeTabId: null,
            contentPanelMode: null,
            openFilePath: null,
            targetLine: null,
            staleOpenFile: false,
          })
          return
        }

        // If we're closing the active tab, pick a neighbor
        if (state.activeTabId === tabId) {
          const newIdx = Math.min(idx, newTabs.length - 1)
          const next = newTabs[newIdx]
          set({
            tabs: newTabs,
            activeTabId: next.id,
            contentPanelMode: "file-tab",
            openFilePath: next.path,
            targetLine: null,
            staleOpenFile: false,
          })
        } else {
          set({ tabs: newTabs })
        }
      },

      closeActiveTab: () => {
        const state = get()
        if (state.activeTabId) {
          get().closeTab(state.activeTabId)
        }
      },

      nextFileTab: () => {
        const state = get()
        if (state.tabs.length < 2 || !state.activeTabId) return
        const idx = state.tabs.findIndex((t) => t.id === state.activeTabId)
        const nextIdx = (idx + 1) % state.tabs.length
        const next = state.tabs[nextIdx]
        get().setActiveTab(next.id)
      },

      previousFileTab: () => {
        const state = get()
        if (state.tabs.length < 2 || !state.activeTabId) return
        const idx = state.tabs.findIndex((t) => t.id === state.activeTabId)
        const prevIdx = (idx - 1 + state.tabs.length) % state.tabs.length
        const prev = state.tabs[prevIdx]
        get().setActiveTab(prev.id)
      },

      setActiveTab: (tabId) => {
        const state = get()
        const tab = state.tabs.find((t) => t.id === tabId)
        if (!tab) return
        set({
          activeTabId: tabId,
          contentPanelMode: "file-tab",
          openFilePath: tab.path,
          targetLine: null,
          staleOpenFile: false,
        })
      },

      clearTargetLine: () => set({ targetLine: null }),

      setChatPanelWidth: (width) =>
        set({
          chatPanelWidth: Math.min(
            CHAT_PANEL_MAX_WIDTH_PERCENT,
            Math.max(CHAT_PANEL_MIN_WIDTH_PERCENT, width),
          ),
        }),

      openPreview: () => {
        const state = get()
        if (state.contentPanelMode === "preview") {
          set({ contentPanelMode: null, openFilePath: null, targetLine: null })
        } else {
          set({ contentPanelMode: "preview", openFilePath: null, activeTabId: null, targetLine: null })
        }
      },

      openDevServerLogs: () => {
        const state = get()
        if (state.contentPanelMode === "devlogs") {
          set({ contentPanelMode: null, openFilePath: null, targetLine: null })
        } else {
          set({ contentPanelMode: "devlogs", openFilePath: null, activeTabId: null, targetLine: null })
        }
      },

      closeContentPanel: () =>
        set({
          contentPanelMode: null,
          openFilePath: null,
          targetLine: null,
          tabs: [],
          activeTabId: null,
          staleOpenFile: false,
        }),

      // Chat todo dock
      setTodoDockOpen: (open) => set({ todoDockOpen: open }),
      setTodoDockAutoOpenSuppressed: (suppressed) => set({ todoDockAutoOpenSuppressed: suppressed }),

      setSharedTimelineViewState: ({ scrollOffsetFromBottom, viewportAnchor }) => {
        const state = get()
        const nextScrollOffset = scrollOffsetFromBottom === undefined
          ? state.sharedTimelineScrollOffsetFromBottom
          : (typeof scrollOffsetFromBottom === "number" && Number.isFinite(scrollOffsetFromBottom) && scrollOffsetFromBottom >= 0
            ? Math.floor(scrollOffsetFromBottom)
            : null)
        const nextViewportAnchor = viewportAnchor === undefined
          ? state.sharedTimelineViewportAnchor
          : normalizeSharedTimelineViewportAnchor(viewportAnchor)

        if (
          nextScrollOffset === state.sharedTimelineScrollOffsetFromBottom
          && sameSharedTimelineViewportAnchor({
            left: nextViewportAnchor,
            right: state.sharedTimelineViewportAnchor,
          })
        ) {
          return
        }

        set({
          sharedTimelineScrollOffsetFromBottom: nextScrollOffset,
          sharedTimelineViewportAnchor: nextViewportAnchor,
        })
      },

      // Debug logs panel
      debugLogsOpen: false,
      setDebugLogsOpen: (open) => set({ debugLogsOpen: open }),
      toggleDebugLogs: () => set((s) => ({ debugLogsOpen: !s.debugLogsOpen })),

      // File tree
      expandedDirs: new Set<string>(),
      toggleDir: (path) =>
        set((s) => {
          const next = new Set(s.expandedDirs)
          if (next.has(path)) {
            next.delete(path)
          } else {
            next.add(path)
          }
          return { expandedDirs: next }
        }),
      setExpandedDirs: (dirs) => set({ expandedDirs: dirs }),

      // Changes panel tree
      changesExpandedDirs: new Set<string>(),
      toggleChangesDir: (path) =>
        set((s) => {
          const next = new Set(s.changesExpandedDirs)
          if (next.has(path)) {
            next.delete(path)
          } else {
            next.add(path)
          }
          return { changesExpandedDirs: next }
        }),
      setChangesExpandedDirs: (dirs) => set({ changesExpandedDirs: dirs }),

      // Recently modified files
      recentlyModifiedFiles: new Map<string, number>(),
      markFileModified: (path) =>
        set((s) => {
          const next = new Map(s.recentlyModifiedFiles)
          next.set(path, Date.now())
          // Clear any existing timer for this path, then set a new one
          const existing = fileModifiedTimers.get(path)
          if (existing) clearTimeout(existing)
          fileModifiedTimers.set(
            path,
            setTimeout(() => {
              fileModifiedTimers.delete(path)
              useSessionUI.setState((s2) => {
                const next2 = new Map(s2.recentlyModifiedFiles)
                next2.delete(path)
                return { recentlyModifiedFiles: next2 }
              })
            }, 3000),
          )
          return { recentlyModifiedFiles: next }
        }),

      // Stale open file
      staleOpenFile: false,
      setStaleOpenFile: (stale) => set({ staleOpenFile: stale }),

      // Git operation state
      gitOperationLoading: false,
      gitOperationError: null,
      setGitOperationLoading: (loading) => set({ gitOperationLoading: loading }),
      setGitOperationError: (error) => set({ gitOperationError: error }),

      // AI commit message generation
      generatedCommitMessage: null,
      commitMessageGenerating: false,
      commitMessageError: null,
      setGeneratedCommitMessage: (msg) => set({ generatedCommitMessage: msg, commitMessageError: null }),
      setCommitMessageGenerating: (loading) => set({ commitMessageGenerating: loading, commitMessageError: null }),
      setCommitMessageError: (error) => set({ commitMessageError: error }),

      // AI PR description generation
      prDescGenerating: false,
      prDescError: null,
      generatedPrTitle: null,
      generatedPrBody: null,
      setPrDescGenerating: (loading) => set({ prDescGenerating: loading, prDescError: null }),
      setPrDescError: (error) => set({ prDescError: error, prDescGenerating: false }),
      setGeneratedPrTitle: (title) => set({ generatedPrTitle: title }),
      setGeneratedPrBody: (body) => set({ generatedPrBody: body }),

      // Quick PR UI presentation state
      dismissedQuickPrOperationIds: {},
      dismissQuickPrOperation: (sessionId, operationId) => {
        if (!sessionId || !operationId) return
        set((state) => ({
          dismissedQuickPrOperationIds: {
            ...state.dismissedQuickPrOperationIds,
            [sessionId]: operationId,
          },
        }))
      },
      clearDismissedQuickPrOperation: (sessionId) => {
        if (!sessionId) return
        set((state) => {
          if (!(sessionId in state.dismissedQuickPrOperationIds)) return state
          const next = { ...state.dismissedQuickPrOperationIds }
          delete next[sessionId]
          return { dismissedQuickPrOperationIds: next }
        })
      },
      // New-session centered mode — not persisted, starts as false each page load.
      // The session page flips this to true when it's an empty session; false once
      // the first message is sent.
      newSessionCentered: false,
      setNewSessionCentered: (centered) => set({ newSessionCentered: centered }),

      // Reset per-session ephemeral state (preserves layout prefs)
      resetEphemeral: () => {
        // Clear all pending file-modified timers to prevent cross-session state mutation
        clearFileModifiedTimers()
        useTerminalStore.getState().reset()
        set({
          terminalVisible: false,
          mobileSurface: "chat",
          contentPanelMode: null,
          openFilePath: null,
          targetLine: null,
          tabs: [],
          activeTabId: null,
          rightSidebarTab: "files" as const,
          rightSidebarChangesAutoSwitchState: "pending" as const,
          expandedDirs: new Set<string>(),
          changesExpandedDirs: new Set<string>(),
          todoDockOpen: false,
          todoDockAutoOpenSuppressed: false,
          sharedTimelineScrollOffsetFromBottom: null,
          sharedTimelineViewportAnchor: null,
          debugLogsOpen: false,
          newSessionCentered: false,
          ...resetTransientUiState(),
        })
      },
    }),
    {
      name: "cord-session-ui",
      version: SESSION_UI_PERSIST_VERSION,
      migrate: normalizePersistedSessionUIState,
      // Only persist layout preferences, not ephemeral state
      partialize: (state) => ({
        leftSidebarOpen: state.leftSidebarOpen,
        leftSidebarWidth: state.leftSidebarWidth,
        rightSidebarOpen: state.rightSidebarOpen,
        rightSidebarWidth: state.rightSidebarWidth,
        terminalHeight: state.terminalHeight,
        chatPanelWidth: state.chatPanelWidth,
        changesViewMode: state.changesViewMode,
        changesViewModeUserSelected: state.changesViewModeUserSelected,
      }),
    },
  ),
)

export function exportSessionLayoutSnapshot(): SessionLayoutSnapshot {
  const state = useSessionUI.getState()
  const normalizedViewportAnchor = normalizeSharedTimelineViewportAnchor(
    state.sharedTimelineViewportAnchor,
  )
  const normalizedScrollOffset = (normalizedViewportAnchor
    ? normalizeNonNegativeSnapshotScrollOffset(state.sharedTimelineScrollOffsetFromBottom)
    : normalizeSnapshotScrollOffset(
      state.sharedTimelineScrollOffsetFromBottom,
    ))
  return {
    terminalVisible: state.terminalVisible,
    mobileSurface: state.mobileSurface,
    contentPanelMode: state.contentPanelMode,
    tabs: state.tabs.map((tab) => ({ ...tab })),
    activeTabId: state.activeTabId,
    rightSidebarOpen: state.rightSidebarOpen,
    rightSidebarTab: state.rightSidebarTab,
    rightSidebarChangesAutoSwitchState: state.rightSidebarChangesAutoSwitchState,
    expandedDirs: Array.from(state.expandedDirs),
    changesExpandedDirs: Array.from(state.changesExpandedDirs),
    todoDockOpen: state.todoDockOpen,
    todoDockAutoOpenSuppressed: state.todoDockAutoOpenSuppressed,
    debugLogsOpen: state.debugLogsOpen,
    sharedTimelineScrollOffsetFromBottom: normalizedScrollOffset,
    sharedTimelineViewportAnchor: normalizedViewportAnchor,
  }
}

export function importSessionLayoutSnapshot(snapshot: SessionLayoutSnapshot): void {
  clearFileModifiedTimers()

  const normalizedViewportAnchor = normalizeSharedTimelineViewportAnchor(snapshot.sharedTimelineViewportAnchor)
  const rawActiveTabId = typeof snapshot.activeTabId === "string" ? snapshot.activeTabId : null
  const normalizedActiveTabId = rawActiveTabId?.replace(/^(file|diff):/, "") ?? null
  const requestedMode = normalizeSnapshotMode(snapshot.contentPanelMode)

  // Deduplicate tabs by id after normalization — old snapshots could have both
  // "file:path" and "diff:path" tabs for the same file which now share the same id.
  // Prefer the raw tab that was actually active, then prefer diff if the legacy
  // panel mode was diff so we restore the intended view.
  const tabsById = new Map<string, ContentTab>()
  if (Array.isArray(snapshot.tabs)) {
    for (const rawTab of snapshot.tabs as unknown[]) {
      const normalizedTab = normalizeSnapshotTab(rawTab)
      if (!normalizedTab) continue
      const existing = tabsById.get(normalizedTab.id)
      if (!existing) {
        tabsById.set(normalizedTab.id, normalizedTab)
        continue
      }

      const raw = rawTab as Record<string, unknown>
      const rawId = typeof raw.id === "string" ? raw.id : ""
      const isRequestedActiveTab = rawId === rawActiveTabId
      const prefersDiffForLegacyMode =
        requestedMode === "file-tab" &&
        rawActiveTabId?.startsWith("diff:") &&
        normalizedActiveTabId === normalizedTab.id &&
        normalizedTab.viewMode === "diff"

      if (isRequestedActiveTab || prefersDiffForLegacyMode) {
        tabsById.set(normalizedTab.id, normalizedTab)
      }
    }
  }
  const tabs = Array.from(tabsById.values())
  const activeTabId = normalizedActiveTabId
  const requestedActiveTab = activeTabId ? tabs.find((tab) => tab.id === activeTabId) ?? null : null
  const fallbackTab = tabs[0] ?? null
  const resolvedTab = requestedMode === "preview" ? null : (requestedActiveTab ?? fallbackTab)
  const contentPanelMode: ContentPanelMode = requestedMode === "preview"
    ? "preview"
    : requestedMode === "devlogs"
      ? "devlogs"
    : resolvedTab
      ? "file-tab"
      : null
  const resolvedActiveTabId = contentPanelMode === "preview"
    ? null
    : contentPanelMode === "devlogs"
      ? null
    : resolvedTab
      ? resolvedTab.id
      : null
  const resolvedOpenFilePath = contentPanelMode === "preview"
    ? null
    : contentPanelMode === "devlogs"
      ? null
    : resolvedTab?.path ?? null

  useSessionUI.setState({
    terminalVisible: normalizeSnapshotBoolean(snapshot.terminalVisible),
    mobileSurface: normalizeSnapshotMobileSurface(snapshot.mobileSurface),
    contentPanelMode,
    tabs,
    activeTabId: resolvedActiveTabId,
    openFilePath: resolvedOpenFilePath,
    targetLine: null,
    rightSidebarOpen: normalizeSnapshotBooleanWithFallback(snapshot.rightSidebarOpen, true),
    rightSidebarTab: snapshot.rightSidebarTab === "changes" ? "changes" : "files",
    rightSidebarChangesAutoSwitchState: normalizeRightSidebarChangesAutoSwitchState(
      snapshot.rightSidebarChangesAutoSwitchState,
    ),
    expandedDirs: new Set(normalizeSnapshotDirArray(snapshot.expandedDirs)),
    changesExpandedDirs: new Set(normalizeSnapshotDirArray(snapshot.changesExpandedDirs)),
    todoDockOpen: normalizeSnapshotBoolean(snapshot.todoDockOpen),
    todoDockAutoOpenSuppressed: normalizeSnapshotBoolean(snapshot.todoDockAutoOpenSuppressed),
    debugLogsOpen: normalizeSnapshotBoolean(snapshot.debugLogsOpen),
    sharedTimelineScrollOffsetFromBottom: normalizedViewportAnchor
      ? normalizeNonNegativeSnapshotScrollOffset(snapshot.sharedTimelineScrollOffsetFromBottom)
      : normalizeSnapshotScrollOffset(snapshot.sharedTimelineScrollOffsetFromBottom),
    sharedTimelineViewportAnchor: normalizedViewportAnchor,
    ...resetTransientUiState(),
  })
}

export function hasPristineSessionLayoutStateForHydration(): boolean {
  const state = useSessionUI.getState()
  return (
    state.terminalVisible === false &&
    state.mobileSurface === "chat" &&
    state.contentPanelMode === null &&
    state.openFilePath === null &&
    state.targetLine === null &&
    state.tabs.length === 0 &&
    state.activeTabId === null &&
    state.rightSidebarOpen === true &&
    state.rightSidebarTab === "files" &&
    state.rightSidebarChangesAutoSwitchState === "pending" &&
    state.expandedDirs.size === 0 &&
    state.changesExpandedDirs.size === 0 &&
    state.todoDockOpen === false &&
    state.todoDockAutoOpenSuppressed === false &&
    state.debugLogsOpen === false &&
    state.sharedTimelineScrollOffsetFromBottom === null &&
    state.sharedTimelineViewportAnchor === null
  )
}

export function saveSessionLayoutSnapshotForSession(sessionId: string): void {
  if (!sessionId) return
  const snapshot = exportSessionLayoutSnapshot()
  sessionLayoutSnapshotsBySessionId.delete(sessionId)
  sessionLayoutSnapshotsBySessionId.set(sessionId, snapshot)
  persistSessionLayoutSnapshotToStorage(sessionId, snapshot)
  pruneSessionLayoutSnapshotsIfNeeded()
}

export function clearSessionLayoutSnapshotForSession(sessionId: string): void {
  if (!sessionId) return
  sessionLayoutSnapshotsBySessionId.delete(sessionId)
  removeSessionLayoutSnapshotFromStorage(sessionId)
}

export function peekSessionLayoutSnapshotForSession(sessionId: string): SessionLayoutSnapshot | null {
  if (!sessionId) return null
  const snapshot = sessionLayoutSnapshotsBySessionId.get(sessionId)
    ?? loadSessionLayoutSnapshotFromStorage(sessionId)
  if (!snapshot) return null

  if (!sessionLayoutSnapshotsBySessionId.has(sessionId)) {
    sessionLayoutSnapshotsBySessionId.set(sessionId, snapshot)
    pruneSessionLayoutSnapshotsIfNeeded()
  }

  return {
    ...snapshot,
    rightSidebarOpen: normalizeSnapshotBooleanWithFallback(snapshot.rightSidebarOpen, true),
    rightSidebarTab: snapshot.rightSidebarTab === "changes" ? "changes" : "files",
    rightSidebarChangesAutoSwitchState: normalizeRightSidebarChangesAutoSwitchState(
      snapshot.rightSidebarChangesAutoSwitchState,
    ),
  }
}

export function restoreSessionLayoutSnapshotForSession(sessionId: string): boolean {
  if (!sessionId) return false
  const snapshot = peekSessionLayoutSnapshotForSession(sessionId)
  if (!snapshot) return false

  importSessionLayoutSnapshot(snapshot)
  return true
}
